/**
 * The action engine: one polymorphic register for NCR, CAR, PAR and OFI records.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The disposition rounds, the USE_AS_IS concession gate and the coded root cause at
 * approval are the server's guards. This module sends what the shapes require and
 * renders the refusals; it does not pre-validate a rule the server owns, because a
 * client that re-implements a guard will one day disagree with it.
 *
 * The register is server-paginated like the findings register.
 */

import { apiJson, listBody, num, optStr, query, str } from "./client";

export interface ActionSummary {
  id: string;
  humanId: string;
  actionType: string;
  actionStatus: string;
  title: string;
  contractorName: string | null;
  originFindingId: string | null;
  targetImplementationDate: string | null;
  actualImplementationDate: string | null;
  dispositionType: string | null;
  concessionReference: string | null;
  rootCauseCategory: string | null;
  roundNo: number;
  closedByName: string | null;
  /** Wave R0 Break 11: the effectiveness review's due date, on the register row too. */
  effectivenessReviewDate: string | null;
}

export interface ActionEvidence {
  id: string;
  seq: number;
  evidenceText: string;
  fileRef: string;
  uploadedAt: string;
}

export interface ActionTransition {
  id: string;
  event: string;
  fromState: string;
  toState: string;
  comment: string | null;
  at: string;
}

export interface ActionDetail extends ActionSummary {
  description: string;
  referenceSpecification: string | null;
  dispositionJustification: string | null;
  dispositionProposedByName: string | null;
  dispositionApprovedByName: string | null;
  rootCauseAnalysis: string | null;
  correctiveActionPlan: string | null;
  responseDueAt: string | null;
  effectivenessReviewRequired: boolean;
  effectivenessOutcome: string | null;
  effectivenessNotes: string | null;
  /** Wave W1.5: the caller submitted it, so approving or issuing it is not theirs. */
  submittedByMe: boolean;
  /** Wave W1.5: the caller proposed the disposition, so deciding or closing is not theirs. */
  dispositionProposedByMe: boolean;
  /**
   * Wave INST ruling 4 and 8: this record's disposition is approved in two hands (USE_AS_IS,
   * REPAIR, or any disposition of a CHANGE_REQUEST). Internal callers only; false otherwise.
   */
  dispositionNeedsAuthority: boolean;
  /** When an operator's approval sent the disposition to the authority (PENDING_AUTHORITY). */
  dispositionAuthorityProposedAt: string | null;
  /** The caller made that proposal, so completing it is another person's act. */
  authorityProposedByMe: boolean;
  /** The caller holds the engineering authority here and may complete the proposal. */
  mayCompleteAuthority: boolean;
  evidence: ActionEvidence[];
  transitions: ActionTransition[];
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function summary(body: Record<string, unknown>): ActionSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    actionType: str(body.action_type),
    actionStatus: str(body.action_status),
    title: str(body.title),
    contractorName: optStr(body.contractor_name),
    originFindingId: optStr(body.origin_finding_id),
    targetImplementationDate: optStr(body.target_implementation_date),
    actualImplementationDate: optStr(body.actual_implementation_date),
    dispositionType: optStr(body.disposition_type),
    concessionReference: optStr(body.concession_reference),
    rootCauseCategory: optStr(body.root_cause_category),
    roundNo: num(body.round_no),
    closedByName: optStr(body.closed_by_name),
    effectivenessReviewDate: optStr(body.effectiveness_review_date),
  };
}

export async function listActions(
  token: string,
  filters: {
    actionType?: string | null;
    actionStatus?: string | null;
    /** The stage/discipline stamp filters (P2): the rows carry both axes. */
    stageId?: string | null;
    disciplineId?: string | null;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: ActionSummary[] }> {
  const body = await apiJson(
    token,
    `/action-requests${query({
      action_type: filters.actionType,
      action_status: filters.actionStatus,
      stage_id: filters.stageId,
      discipline_id: filters.disciplineId,
      limit: filters.limit,
      offset: filters.offset,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(summary) };
}

export async function getAction(
  token: string,
  actionRequestId: string,
  signal?: AbortSignal,
): Promise<ActionDetail> {
  return apiJson(
    token,
    `/action-requests/${actionRequestId}`,
    (raw) => {
      const body = assertRecord(raw);
      const evidence = Array.isArray(body.evidence) ? body.evidence : [];
      const transitions = Array.isArray(body.transitions) ? body.transitions : [];
      return {
        ...summary(body),
        description: str(body.description),
        referenceSpecification: optStr(body.reference_specification),
        dispositionJustification: optStr(body.disposition_justification),
        dispositionProposedByName: optStr(body.disposition_proposed_by_name),
        dispositionApprovedByName: optStr(body.disposition_approved_by_name),
        rootCauseAnalysis: optStr(body.root_cause_analysis),
        correctiveActionPlan: optStr(body.corrective_action_plan),
        responseDueAt: optStr(body.response_due_at),
        effectivenessReviewRequired: body.effectiveness_review_required === true,
        effectivenessOutcome: optStr(body.effectiveness_outcome),
        effectivenessNotes: optStr(body.effectiveness_notes),
        submittedByMe: body.submitted_by_me === true,
        dispositionProposedByMe: body.disposition_proposed_by_me === true,
        dispositionNeedsAuthority: body.disposition_needs_authority === true,
        dispositionAuthorityProposedAt: optStr(body.disposition_authority_proposed_at),
        authorityProposedByMe: body.authority_proposed_by_me === true,
        mayCompleteAuthority: body.may_complete_authority === true,
        evidence: evidence.map((entry) => {
          const item = assertRecord(entry);
          return {
            id: str(item.id),
            seq: num(item.seq),
            evidenceText: str(item.evidence_text),
            fileRef: str(item.file_ref),
            uploadedAt: str(item.uploaded_at),
          };
        }),
        transitions: transitions.map((entry) => {
          const transition = assertRecord(entry);
          return {
            id: str(transition.id),
            event: str(transition.event),
            fromState: str(transition.from_state),
            toState: str(transition.to_state),
            comment: optStr(transition.comment),
            at: str(transition.at),
          };
        }),
      };
    },
    { signal },
  );
}

async function post(token: string, path: string, body: unknown): Promise<Record<string, unknown>> {
  return apiJson(token, path, (raw) => assertRecord(raw), { method: "POST", body });
}

/** Approve or refuse the record's content before it is issued. Refusal demotes a promoted finding. */
export async function decideContent(
  token: string,
  actionRequestId: string,
  event: "approve" | "reject",
  comment: string | null,
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/${event}`, { comment });
}

/** Issue the approved record to the contractor. The response clock starts here. */
export async function issue(token: string, actionRequestId: string): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/issue`, {});
}

/** The contractor's disposition round. USE_AS_IS without a concession reference is refused. */
export async function proposeDisposition(
  token: string,
  actionRequestId: string,
  payload: {
    disposition_type: string;
    disposition_justification: string;
    concession_reference?: string | null;
    root_cause_category?: string | null;
    root_cause_analysis?: string | null;
    corrective_action_plan?: string | null;
    target_implementation_date?: string | null;
  },
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/dispositions`, payload);
}

/**
 * The operator's decision on a disposition round. A return demands its comment. Wave INST
 * ruling 4: for a disposition that needs the engineering authority, the operator's approve
 * proposes it (PENDING_AUTHORITY) and the authority's approve on the same route completes
 * it (AGREED); a return from PENDING_AUTHORITY sends it back to the contractor (ISSUED).
 */
export async function decideDisposition(
  token: string,
  actionRequestId: string,
  event: "approve" | "reject",
  payload: {
    root_cause_code_id?: string | null;
    root_cause_category?: string | null;
    corrective_action_plan?: string | null;
    comment?: string | null;
  },
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/dispositions/${event}`, payload);
}

/** The contractor declares implementation with its evidence rows. */
export async function declareImplementation(
  token: string,
  actionRequestId: string,
  evidence: { evidence_text: string; file_ref: string }[],
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/implementation`, {
    evidence_items: evidence,
  });
}

/**
 * Verify closure. Wave R0 Break 11: the verifier says whether the closure waits on an
 * effectiveness review (due in 90 days) or closes now; omitted, the record's stored flag
 * applies (Director ruling 2). The dialog always sends the verifier's word.
 */
export async function verifyClosure(
  token: string,
  actionRequestId: string,
  payload: {
    closure_notes?: string | null;
    comment?: string | null;
    effectiveness_review_required?: boolean;
  },
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/verify-closure`, payload);
}

/** Answer the effectiveness review. Not effective offers a follow-up action at the engine. */
export async function recordEffectiveness(
  token: string,
  actionRequestId: string,
  payload: { effectiveness_outcome: string; effectiveness_notes?: string | null },
): Promise<void> {
  await post(token, `/action-requests/${actionRequestId}/effectiveness`, payload);
}
